import React, { useState, useEffect } from "react";
import { Drawer } from "../../../core/components/drawer/Drawer";
import { SvgIcon } from "../../../core/components/svgIcon/SvgIcon";
import { useUnit } from "effector-react";
import {
  $sectorsTerms,
  $expertiseTerms,
  $lookingForTerms,
  $programmeTerms,
  fetchTermsRequested,
} from "@/core/store/terms";
import "./FiltersDrawer.scss";

export interface FiltersDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  selectedFilters?: FilterState;
}

export interface FilterState {
  fromDate: string;
  toDate: string;
  categories: string[]; // Category names
  programmes: string[]; // Term IDs (tids)
  sectors: string[]; // Term IDs (tids)
  expertise: string[]; // Term IDs (tids)
  lookingFor: string[]; // Term IDs (tids)
  categorySearch: string;
  programmeSearch: string;
  sectorSearch: string;
}

const initialFilterState: FilterState = {
  fromDate: "",
  toDate: "",
  categories: [],
  programmes: [],
  sectors: [],
  expertise: [],
  lookingFor: [],
  categorySearch: "",
  programmeSearch: "",
  sectorSearch: "",
};

export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({
  isOpen,
  onClose,
  onApply,
  selectedFilters = initialFilterState,
}) => {
  const [filters, setFilters] = useState<FilterState>(selectedFilters);
  const [expandedSectors, setExpandedSectors] = useState<string[]>([]);
  const [showMoreProgrammes, setShowMoreProgrammes] = useState(false);
  const [showMoreExpertise, setShowMoreExpertise] = useState(false);
  const [showMoreSectors, setShowMoreSectors] = useState(false);

  // Get terms from the store
  const [
    sectorsTerms,
    expertiseTerms,
    lookingForTerms,
    programmeTerms,
    fetchTerms,
  ] = useUnit([
    $sectorsTerms,
    $expertiseTerms,
    $lookingForTerms,
    $programmeTerms,
    fetchTermsRequested,
  ]);

  // Fetch terms if not already loaded
  useEffect(() => {
    if (!sectorsTerms.data.length && !sectorsTerms.loading) {
      fetchTerms({ vocId: "sectors" });
    }
    if (!expertiseTerms.data.length && !expertiseTerms.loading) {
      fetchTerms({ vocId: "expertise" });
    }
    if (!lookingForTerms.data.length && !lookingForTerms.loading) {
      fetchTerms({ vocId: "looking_for" });
    }
    if (!programmeTerms.data.length && !programmeTerms.loading) {
      fetchTerms({ vocId: "programme" });
    }
  }, [
    sectorsTerms,
    expertiseTerms,
    lookingForTerms,
    programmeTerms,
    fetchTerms,
  ]);

  // Update filters when selectedFilters prop changes
  useEffect(() => {
    setFilters(selectedFilters);
  }, [selectedFilters]);

  const handleCheckboxChange = (category: keyof FilterState, value: string) => {
    if (Array.isArray(filters[category])) {
      const currentValues = filters[category] as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      setFilters({ ...filters, [category]: newValues });
    }
  };

  const handleSectorToggle = (sector: string) => {
    setExpandedSectors((prev) =>
      prev.includes(sector)
        ? prev.filter((s) => s !== sector)
        : [...prev, sector],
    );
  };

  const filteredSectors = sectorsTerms.data.filter((sector) =>
    sector.name.toLowerCase().includes(filters.sectorSearch.toLowerCase()),
  );

  const filteredProgrammes = programmeTerms.data.filter((programme) =>
    programme.name
      .toLowerCase()
      .includes(filters.programmeSearch.toLowerCase()),
  );

  // Group sectors by parent/child relationship (for nested display)
  const parentSectors = filteredSectors.filter(
    (s) => !s.parent || s.parent === "0",
  );
  const childSectorsByParent: Record<string, typeof filteredSectors> = {};

  filteredSectors.forEach((sector) => {
    if (sector.parent && sector.parent !== "0") {
      if (!childSectorsByParent[sector.parent]) {
        childSectorsByParent[sector.parent] = [];
      }
      childSectorsByParent[sector.parent].push(sector);
    }
  });

  const handleClearAll = () => {
    setFilters(initialFilterState);
    onApply(initialFilterState);
    setExpandedSectors([]);
    // onClose();
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.fromDate || filters.toDate) count++;
    count += filters.categories.length;
    count += filters.programmes.length;
    count += filters.sectors.length;
    count += filters.expertise.length;
    count += filters.lookingFor.length;
    return count;
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} title="Filters" width="400px">
      <div className="filters-drawer">
        <p className="filters-description">
          Apply filters to ideas.
          {getActiveFiltersCount() > 0 && (
            <span className="active-count">
              {" "}
              ({getActiveFiltersCount()} active)
            </span>
          )}
        </p>
        {/* Date Range */}
        {/* <div className="filter-section">
          <h3>Date Range</h3>
          <div className="date-range">
            <div className="date-field">
              <label>From</label>
              <div className="date-input-wrapper">
                <SvgIcon icon="icon-calendar" />
                <input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={filters.fromDate}
                  onChange={(e) =>
                    setFilters({ ...filters, fromDate: e.target.value })
                  }
                />
              </div>
            </div>
            <div className="date-field">
              <label>To</label>
              <div className="date-input-wrapper">
                <SvgIcon icon="icon-calendar" />
                <input
                  type="text"
                  placeholder="DD/MM/YYYY"
                  value={filters.toDate}
                  onChange={(e) =>
                    setFilters({ ...filters, toDate: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        </div> */}
        {/* Categories */}
        {/* <div className="filter-section">
          <h3>Categories</h3>
          <div className="search-wrapper">
            <SvgIcon icon="icon-search" />
            <input
              type="text"
              placeholder="Search Categories"
              value={filters.categorySearch}
              onChange={(e) => setFilters({...filters, categorySearch: e.target.value})}
            />
          </div>
          <div className="checkbox-list">
            {filteredCategories.slice(0, showMoreCategories ? filteredCategories.length : 3).map((category) => (
              <label key={category} className="checkbox-item">
                <input
                  type="checkbox"
                  checked={filters.categories.includes(category)}
                  onChange={() => handleCheckboxChange('categories', category)}
                />
                <span className="checkmark"></span>
                {category}
              </label>
            ))}
            {filteredCategories.length > 3 && (
              <button className="show-more-btn" onClick={() => setShowMoreCategories(!showMoreCategories)}>
                Show {showMoreCategories ? 'less' : `${filteredCategories.length - 3} more`}
              </button>
            )}
          </div>
        </div> */}
        {/* Looking For */}
        <div className="filter-section">
          <h3>Looking For</h3>
          <div className="checkbox-list">
            {lookingForTerms.loading ? (
              <div>Loading options...</div>
            ) : (
              lookingForTerms.data.map((item) => (
                <label key={item.tid} className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={filters.lookingFor.includes(item.tid)}
                    onChange={() =>
                      handleCheckboxChange("lookingFor", item.tid)
                    }
                  />
                  <span className="checkmark"></span>
                  {item.name}
                </label>
              ))
            )}
          </div>
        </div>
        {/* Expertise */}
        <div className="filter-section">
          <h3>Fields of expertise</h3>
          <div className="checkbox-list">
            {expertiseTerms.loading ? (
              <div>Loading expertise...</div>
            ) : (
              expertiseTerms.data
                .slice(0, showMoreExpertise ? expertiseTerms.data.length : 4)
                .map((expertise) => (
                  <label key={expertise.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.expertise.includes(expertise.tid)}
                      onChange={() =>
                        handleCheckboxChange("expertise", expertise.tid)
                      }
                    />
                    <span className="checkmark"></span>
                    {expertise.name}
                  </label>
                ))
            )}
            {!expertiseTerms.loading && expertiseTerms.data.length > 4 && (
              <button
                className="show-more-btn"
                onClick={() => setShowMoreExpertise(!showMoreExpertise)}
              >
                Show{" "}
                {showMoreExpertise
                  ? "less"
                  : `${expertiseTerms.data.length - 4} more`}
              </button>
            )}
          </div>
        </div>{" "}
        {/* Programmes */}
        <div className="filter-section">
          <h3>Programmes</h3>
          <div className="search-wrapper">
            <SvgIcon icon="icon-search" />
            <input
              type="text"
              placeholder="Search Programmes"
              value={filters.programmeSearch}
              onChange={(e) =>
                setFilters({ ...filters, programmeSearch: e.target.value })
              }
            />
          </div>
          <div className="checkbox-list">
            {programmeTerms.loading ? (
              <div>Loading programmes...</div>
            ) : (
              filteredProgrammes
                .slice(0, showMoreProgrammes ? filteredProgrammes.length : 3)
                .map((programme) => (
                  <label key={programme.tid} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={filters.programmes.includes(programme.tid)}
                      onChange={() =>
                        handleCheckboxChange("programmes", programme.tid)
                      }
                    />
                    <span className="checkmark"></span>
                    {programme.name}
                  </label>
                ))
            )}
            {!programmeTerms.loading && filteredProgrammes.length > 3 && (
              <button
                className="show-more-btn"
                onClick={() => setShowMoreProgrammes(!showMoreProgrammes)}
              >
                Show{" "}
                {showMoreProgrammes
                  ? "less"
                  : `${filteredProgrammes.length - 3} more`}
              </button>
            )}
          </div>
        </div>
        {/* Sectors */}
        <div className="filter-section">
          <h3>Sectors</h3>
          <div className="search-wrapper">
            <SvgIcon icon="icon-search" />
            <input
              type="text"
              placeholder="Search Sectors"
              value={filters.sectorSearch}
              onChange={(e) =>
                setFilters({ ...filters, sectorSearch: e.target.value })
              }
            />
          </div>
          <div className="checkbox-list">
            {sectorsTerms.loading ? (
              <div>Loading sectors...</div>
            ) : (
              parentSectors
                .slice(0, showMoreSectors ? parentSectors.length : 10)
                .map((sector) => {
                  const hasChildren =
                    childSectorsByParent[sector.tid] &&
                    childSectorsByParent[sector.tid].length > 0;
                  return (
                    <div key={sector.tid}>
                      <div className="checkbox-item expandable">
                        {hasChildren && (
                          <button
                            className="expand-btn"
                            onClick={() => handleSectorToggle(sector.tid)}
                          >
                            <SvgIcon
                              icon={
                                expandedSectors.includes(sector.tid)
                                  ? "icon-chevron-down"
                                  : "icon-chevron-right"
                              }
                            />
                          </button>
                        )}
                        <label
                          className={`checkbox-label ${!hasChildren ? "no-expand" : ""}`}
                        >
                          <input
                            type="checkbox"
                            checked={filters.sectors.includes(sector.tid)}
                            onChange={() =>
                              handleCheckboxChange("sectors", sector.tid)
                            }
                          />
                          <span className="checkmark"></span>
                          {sector.name}
                        </label>
                      </div>
                      {expandedSectors.includes(sector.tid) && hasChildren && (
                        <div className="subsector-list">
                          {childSectorsByParent[sector.tid].map((subsector) => (
                            <label
                              key={subsector.tid}
                              className="checkbox-item subsector"
                            >
                              <input
                                type="checkbox"
                                checked={filters.sectors.includes(
                                  subsector.tid,
                                )}
                                onChange={() =>
                                  handleCheckboxChange("sectors", subsector.tid)
                                }
                              />
                              <span className="checkmark"></span>
                              {subsector.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
            {!sectorsTerms.loading && parentSectors.length > 10 && (
              <button
                className="show-more-btn"
                onClick={() => setShowMoreSectors(!showMoreSectors)}
              >
                Show{" "}
                {showMoreSectors ? "less" : `${parentSectors.length - 10} more`}
              </button>
            )}
          </div>
        </div>
        {/* Actions */}
        <div className="filter-actions">
          <button className="clear-btn" onClick={handleClearAll}>
            Clear all
          </button>
          <div className="action-buttons">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button className="apply-btn" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
